import React, { useState, useEffect, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { AuthContext } from "../../../context/AuthContext";
import AuthModal from "../../../components/modals/AuthModal/AuthModal";
import axios from "axios";
import config from "../../../config";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import "./KitchenCard.css";

const KitchenCard = ({ kitchen, distance, status, showToast }) => {
  const { Title, City, DayRate, HourlyRate, BookingType, Images, KitchenID } =
    kitchen;
  const { user, token, favourites, setFavourites } = useContext(AuthContext);
  const [isFavourite, setIsFavourite] = useState(false);
  const defaultImage =
    "https://airkitchenz-assets.s3.us-west-1.amazonaws.com/assets/KitchenComingSoon.png";
  const imageArray = Images && Images.length > 0 ? Images : [defaultImage];

  const totalImages = imageArray.length;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartXRef = useRef(null);
  const pendingFavourite = useRef(null); 

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndX;

    if (diff > 50) handleNext(e);
    else if (diff < -50) handlePrev(e);
  };

  // State mapping for city formatting
  const stateMap = {
    AL: "Alabama",
    AK: "Alaska",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    FL: "Florida",
    GA: "Georgia",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming",
  };

  const formatCity = (cityString) => {
    if (!cityString) return "";
    const parts = cityString.split(",");
    if (parts.length >= 2) {
      const city = parts[0].trim();
      const stateAbbr = parts[1].trim().toUpperCase();
      const fullState = stateMap[stateAbbr] || stateAbbr;
      return `${city}, ${fullState}`;
    }
    return cityString;
  };

  useEffect(() => {
    if (user && favourites) {
      setIsFavourite(favourites.includes(KitchenID));
    } else {
      setIsFavourite(false);
    }
  }, [user, favourites, KitchenID]);

  const handleFavouriteToggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      pendingFavourite.current = e;
      setAuthMode("login");
      setAuthModalOpen(true);
      return;
    }

    const previousFavouriteState = isFavourite;
    setIsFavourite(!isFavourite);

    try {
      if (previousFavouriteState) {
        await axios.post(
          `${config.apiBaseUrl}/api/auth/unfavourite`,
          { userID: user.userID, kitchenID: KitchenID },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFavourites((prev) => prev.filter((id) => id !== KitchenID));
        showToast?.("Removed from favourites", "info");
      } else {
        await axios.post(
          `${config.apiBaseUrl}/api/auth/favourites`,
          { userID: user.userID, kitchenID: KitchenID },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFavourites((prev) => [...prev, KitchenID]);
        showToast?.("Added to favourites!", "success");
      }
    } catch (error) {
      console.error(
        "Error toggling favourite:",
        error.response?.data || error.message
      );
      setIsFavourite(previousFavouriteState);
      setFavourites((prev) =>
        previousFavouriteState
          ? [...prev, KitchenID].filter(
              (id, idx, self) => self.indexOf(id) === idx
            )
          : prev.filter((id) => id !== KitchenID)
      );
    }
  };

  const closeAuthModal = () => {
    setAuthModalOpen(false);
    if (pendingFavourite.current && token) {
      handleFavouriteToggle(pendingFavourite.current);
      pendingFavourite.current = null;
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? totalImages - 1 : prevIndex - 1
    );
  };

  const handleNext = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentImageIndex((prevIndex) =>
      prevIndex === totalImages - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <Link to={`/kitchen/${KitchenID}`} className="kitchen-card-link">
      <Card
        className="kitchen-card"
        sx={{
          overflow: "hidden",
          transition: "transform 0.3s ease",
          cursor: "pointer",
          position: "relative",
          boxShadow: "none",
          border: "none",
          "&:hover": {
            transform: "translateY(-3px)",
          },
        }}
      >
        {/* Favourite Button */}
        <Box
          className={`favourite-btn ${isFavourite ? "active" : ""}`}
          sx={{
            position: "absolute",
            top: 7,
            right: 7,
            zIndex: 10,
          }}
        >
          <motion.div
            whileHover={{ scale: 1.1 }}
            animate={
              isFavourite
                ? { scale: [1, 1.3, 1] }
                : { rotate: [0, -10, 10, -10, 0] }
            }
            transition={
              isFavourite
                ? { duration: 0.3 }
                : { duration: 0.3, times: [0, 0.25, 0.5, 0.75, 1] }
            }
          >
            <IconButton
              onClick={handleFavouriteToggle}
              aria-label={
                isFavourite ? "Remove from Favourites" : "Add to Favourites"
              }
              sx={{
                bgcolor: "transparent",
                p: 1,
              }}
            >
              {isFavourite ? (
                <FaHeart size={24} className="favourite-icon" />
              ) : (
                <FaRegHeart size={24} className="favourite-icon" />
              )}
            </IconButton>
          </motion.div>
        </Box>

        {/* Image Slider */}
        <Box className="image-container">
          <Box
            className="image-slider"
            sx={{
              transform: `translateX(-${currentImageIndex * 100}%)`,
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {imageArray.map((src, index) => (
              <CardMedia
                key={index}
                component="img"
                image={src}
                alt={`${Title} ${index + 1}`}
                className="browse-kitchen-image"
                loading="lazy"
              />
            ))}
          </Box>
          {totalImages > 1 && (
            <>
              <button className="img-arrow arrow-left" onClick={handlePrev}>
                ❮
              </button>
              <button className="img-arrow arrow-right" onClick={handleNext}>
                ❯
              </button>
            </>
          )}

          <Box
            className="dot-indicator"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            position="absolute"
            bottom="10px"
            left="50%"
            sx={{ transform: "translateX(-50%)", zIndex: 2 }}
          >
            {kitchen?.Images?.map((_, index) => (
              <span
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor:
                    currentImageIndex === index ? "#e63946" : "#ccc",
                  display: "inline-block",
                  transition: "background-color 0.3s ease",
                }}
              />
            ))}
          </Box>
          {status === "COMING SOON" && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255, 255, 255, 0.7)",
                zIndex: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#000",
                  fontWeight: "bold",
                  transform: "rotate(-45deg)",
                  fontSize: "1.5rem",
                }}
              >
                COMING SOON
              </Typography>
            </Box>
          )}
        </Box>

        {/* Kitchen Info */}
        <CardContent
          className="kitchen-info"
          sx={{
            p: 0,
            mt: "5px",
            mb: "7px",
            "&:last-child": { pb: 0 },
          }}
        >
          <Box>
            <Typography
              className="kitchen-title"
              sx={{
                fontWeight: "bold",
                fontSize: "1.1rem",
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              {formatCity(City)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.2,
                mt: "2px",
              }}
            >
              {BookingType === "hour"
                ? `$${HourlyRate} per hour`
                : `$${DayRate} per day`}
            </Typography>
            {distance && (
              <Typography
                className="kitchen-distance"
                variant="body2"
                sx={{ mt: "2px", lineHeight: 1.2 }}
              >
                {distance} miles away
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
      <AuthModal
        open={authModalOpen}
        onClose={closeAuthModal}
        initialMode={authMode}
      />
    </Link>
  );
};

export default KitchenCard;
